import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useNavigate,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../app.css?url";
import interLatinFont from "../assets/fonts/inter-latin.woff2?url";
import spaceGroteskLatinFont from "../assets/fonts/space-grotesk-latin.woff2?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { Layout } from "../components/Layout";
import { WipBanner } from "../components/WipBanner";
import { DeferredAdminLogin } from "@/components/DeferredAdminLogin";
import { DeferredToaster } from "@/components/DeferredToaster";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { runWhenIdle } from "@/lib/idle";
import { errorMessage } from "@/lib/error-capture";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <div className="mb-6"><WipBanner /></div>
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: unknown; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "google-site-verification", content: "jTiY30pHl1Vy-gyjzxdQGvqU26DLcJDZeOCQ4wmYPw8" },
      { title: "Restlicht.FYI" },
      { name: "description", content: "Restlicht.FYI - Harm-Reduction-Informationen, Cannabis-Reviews und persönlicher Blog." },
      { property: "og:site_name", content: "Restlicht.FYI" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      // Fonts used above the fold: preloaded so they start in parallel with the CSS
      // instead of after it. Always needs crossOrigin, even for same-origin fonts.
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: spaceGroteskLatinFont,
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: interLatinFont,
        crossOrigin: "anonymous",
      },
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
      { rel: "icon", type: "image/png", sizes: "64x64", href: "/favicon.png" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      { rel: "icon", type: "image/x-icon", href: "/favicon.ico" },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="de">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const navigate = useNavigate();
  const router = useRouter();

  // Sign-out cleanup needs the Supabase client (~30 KiB gzip with auth, realtime and
  // storage), which anonymous visitors never use. Attach the listener only when a
  // stored session exists (after idle) or on the first user interaction, since every
  // login flow needs one. Visitors who just read never download the client here.
  useEffect(() => {
    let started = false;
    let cancelled = false;
    let unsubscribe: (() => void) | undefined;
    const interactionEvents = ["pointerdown", "keydown", "touchstart"] as const;

    const removeInteractionListeners = () => {
      for (const name of interactionEvents) window.removeEventListener(name, start);
    };

    function start() {
      if (started) return;
      started = true;
      removeInteractionListeners();
      import("@/integrations/supabase/client")
        .then(({ supabase }) => {
          if (cancelled) return;
          const {
            data: { subscription },
          } = supabase.auth.onAuthStateChange((event, session) => {
            // The listener is attached late, so a sign-out in another tab can happen
            // before it exists. Subscribing then only reports INITIAL_SESSION without a
            // session; treat that as a sign-out while an authenticated route is shown.
            const lostSessionBeforeSubscribe =
              event === "INITIAL_SESSION" &&
              !session &&
              router.state.matches.some((match) => match.routeId.startsWith("/_authenticated"));
            if (event === "SIGNED_OUT" || lostSessionBeforeSubscribe) {
              queryClient.cancelQueries();
              queryClient.clear();
              navigate({ to: "/", replace: true });
            }
          });
          unsubscribe = () => subscription.unsubscribe();
        })
        .catch(() => {});
    }

    let hasStoredSession = false;
    try {
      hasStoredSession = Object.keys(window.localStorage).some((key) =>
        /^sb-.+-auth-token$/.test(key),
      );
    } catch {
      // Storage can be unavailable (private mode); fall back to the interaction trigger.
    }

    let cancelIdle: (() => void) | undefined;
    if (hasStoredSession) {
      cancelIdle = runWhenIdle(start);
    } else {
      for (const name of interactionEvents) window.addEventListener(name, start, { passive: true });
    }

    return () => {
      cancelled = true;
      cancelIdle?.();
      removeInteractionListeners();
      unsubscribe?.();
    };
  }, [queryClient, navigate, router]);

  return (
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <div className="rl-root">
          <Layout>
            {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
            <Outlet />
          </Layout>
          <DeferredAdminLogin />
          <DeferredToaster />
        </div>
      </LanguageProvider>
    </QueryClientProvider>
  );
}
